import React, { useState, useEffect, useReducer } from 'react';
import { Lock, User, Mail, Phone, Gift, ArrowLeftRight, PinIcon, ArrowLeft } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
// import config
import theme from '../config/theme';
// import lib
import handleReducer, { textChange, onBlur } from '../lib/handleChange';
import isEmpty from '../lib/isEmpty';
import toastAlert from '../lib/toastAlert';
// import validation
import { registerValid } from '../validation/auth.validation';
// import services
import { register } from '../services/auth.service';
// import components
import Input from '../custom/Input';
import ErrorSpan from '../custom/ErrorSpan';
import EyeIcon from '../custom/EyeIcon';
import ButtonLoader from '../custom/ButtonLoader';

const initialFormValue = {
    userName: '',
    email: '',
    password: '',
    phone: '',
    referralCode: '',
    pinNumber: '',
    side: ''
};

const Register = () => {
    // hooks
    const navigate = useNavigate();
    const { search } = useLocation();
    const query = new URLSearchParams(search);
    const refCode = query.get('ref');
    // state
    const [formValue, setFormValue] = useReducer(handleReducer, initialFormValue);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [loader, setLoader] = useState(false);

    const { userName, email, password, phone, referralCode, pinNumber, side } = formValue;

    // function
    const handleChange = e => textChange(e, setFormValue);
    const handleBlur = e => onBlur(e, setTouched);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoader(true);
        const { status, message, errors } = await register(formValue)
        if (status === true) {
            toastAlert('success', message, 'register')
            navigate('/login')
        } else if (errors) {
            setErrors(errors)
        } else if (message) toastAlert('error', message, 'register')
        setLoader(false);
    };

    useEffect(() => {
        const { status, errors } = registerValid(formValue);
        if (!status) setErrors(errors);
        else setErrors({});
    }, [formValue]);

    useEffect(() => {
        if (!isEmpty(refCode)) {
            setFormValue({ type: 'SET', value: { ...formValue, ...{ referralCode: refCode } } })
        }
    }, [refCode])

    return (
        <div style={{ background: `linear-gradient(135deg, ${theme.colors.bg.primary}, ${theme.colors.bg.tertiary})` }} className="min-h-screen flex items-center justify-center p-4">
            <div style={{ background: theme.colors.bg.secondary }} className="rounded-2xl shadow-2xl w-full max-w-md p-8 border border-cyan-500/20 relative">
                {/* Back Button */}
                <button
                    onClick={() => navigate('/')}
                    className="absolute top-6 left-6 p-2 bg-cyan-500/10 hover:bg-cyan-500/20 rounded-lg transition-colors group"
                    title="Back to Home"
                >
                    <ArrowLeft className="w-5 h-5 text-cyan-400 group-hover:text-cyan-300" />
                </button>
                <div className="text-center mb-6">
                    <div className="inline-block p-3 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-full mb-4">
                        <User className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-white">Register</h1>
                    <p className="text-gray-400 mt-2">Create your account to get started</p>
                </div>

                {/* Form */}
                <form className="space-y-4">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Pin Number</label>
                        <div className="relative">
                            <PinIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type="text"
                                name="pinNumber"
                                value={pinNumber}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter pin number"
                                errors={errors}
                                touched={touched}
                            />
                        </div>
                        <ErrorSpan errors={errors} touched={touched} fieldName={'pinNumber'} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Full Name</label>
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type="text"
                                name="userName"
                                value={userName}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter your full name"
                                errors={errors}
                                touched={touched}
                            />
                        </div>
                        <ErrorSpan errors={errors} touched={touched} fieldName={'userName'} />
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Email</label>
                        <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type="text"
                                name="email"
                                value={email}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter your email"
                                errors={errors}
                                touched={touched}
                            />
                        </div>
                        <ErrorSpan errors={errors} touched={touched} fieldName={'email'} />
                    </div>

                    {/* Phone */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Phone Number</label>
                        <div className="relative">
                            <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type="text"
                                name="phone"
                                value={phone}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter your phone number"
                                errors={errors}
                                touched={touched}
                            />
                        </div>
                        <ErrorSpan errors={errors} touched={touched} fieldName={'phone'} />
                    </div>

                    {/* Password */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={password}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="••••••••"
                                errors={errors}
                                touched={touched}
                            />
                            <EyeIcon showPassword={showPassword} setShowPassword={setShowPassword} />
                        </div>
                        <ErrorSpan errors={errors} touched={touched} fieldName={'password'} />
                    </div>

                    {/* Referral Code */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">Referral Code</label>
                        <div className="relative">
                            <Gift className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <Input
                                type="text"
                                name="referralCode"
                                value={referralCode}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                placeholder="Enter referral code"
                                errors={errors}
                                touched={touched}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                            Select Side
                        </label>
                        <div className="relative">
                            <ArrowLeftRight className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <select
                                name="side"
                                value={side}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                style={{ background: theme.colors.bg.primary }}
                                className={`w-full pl-10 pr-4 py-2 border rounded-lg text-white focus:ring-2 focus:ring-cyan-500 focus:border-transparent ${errors.side && touched.side ? 'border-red-500' : 'border-cyan-500/30'
                                    }`}
                            >
                                <option value="" disabled className="text-gray-500">Choose your side</option>
                                <option value="left" className="text-white">Left</option>
                                <option value="right" className="text-white">Right</option>
                            </select>
                        </div>
                        <ErrorSpan errors={errors} touched={touched} fieldName={'side'} />
                    </div>

                    {/* Submit Button */}
                    <ButtonLoader
                        type="button"
                        onClick={handleSubmit}
                        loader={loader}
                        disabled={!isEmpty(errors)}
                        btnName='Create Account'
                    />
                </form>

                {/* Footer */}
                <div className="mt-6 text-center">
                    <p className="text-sm text-gray-400">
                        Already have an account?{' '}
                        <Link to="/login" className="text-cyan-400 hover:text-cyan-300 font-semibold">
                            Sign In
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Register;